import { DateTime } from 'luxon';
import React, { useContext, useEffect, useMemo } from 'react';
import { createContext } from 'react';
import { GetFundRatesResponse, Rate, useGetFundRates } from '../../api/funds-robot/getFundRates';
import {
    MonthlyReturnRatesResponse,
    useGetModelPortfoliosMonthlyReturns,
} from '../../api/funds-robot/getModelPortfolioMonthlyReturns';
import { AssetRates } from '../../types/Asset';
import {
    getComparableChunk,
    indexedFundTimeValueDataPoints,
    indexedPortfolioTimeValueDataPoints,
    thinnedOutOnDate,
    timeValueDataPointsWithinInterval,
} from '../../utils/functions';
import { FundsSelectionContext } from './FundsSelectionContext';
import { ChartIntervalContext } from './ChartIntervalContext';
import { TimeValueDataPoint } from '../../components/funds-robot/comparison-screen/comparison-chart/ValueGrowthChart';

export interface FundRatesContextType {
    isLoading: boolean;
    rates: { 
        all: (AssetRates | undefined)[];
        get: (id: string) => AssetRates | undefined
    };
}

export const FundRatesContext = createContext<FundRatesContextType>({
    isLoading: true,
    rates: {
        all: [],
        get: undefined!,
    },
});

export const WithFundRates: React.FC<{children?: React.ReactNode}> = ({ children }) => {
    const fundsSelectionContext = useContext(FundsSelectionContext);
    const chartIntervalContext = useContext(ChartIntervalContext);

    const selectionKey = fundsSelectionContext.funds.selectedIds.reduce((total, id) => {
        return total + id;
    }, '');

    // This is ugly, but since we cannot use useQuery dynamically, we need to "hard code" 3 useQueries, even of only 2 funds are selected.
    // This ensures we use caching per fundrates query.
    const getFirstFundRatesQuery = useGetFundRates(
        fundsSelectionContext.selectionType === 'Stock' || fundsSelectionContext.selectionType === 'Bond'
            ? fundsSelectionContext.funds.selectedIds[0]
            : undefined,
    );
    const getSecondFundRatesQuery = useGetFundRates(
        fundsSelectionContext.selectionType === 'Stock' || fundsSelectionContext.selectionType === 'Bond'
            ? fundsSelectionContext.funds.selectedIds[1]
            : undefined,
    );
    const getThirdFundRatesQuery = useGetFundRates(
        fundsSelectionContext.selectionType === 'Stock' || fundsSelectionContext.selectionType === 'Bond'
            ? fundsSelectionContext.funds.selectedIds[2]
            : undefined,
    );

    // This is maybe even more ugly; For caching purposes, we also need 3 queries looking for the modelportfolio rates, if
    // Modelportfolios are selected.
    const getFirstModelPortfolioReturnsQuery = useGetModelPortfoliosMonthlyReturns(
        fundsSelectionContext.selectionType === 'Portfolio' ? fundsSelectionContext.funds.selectedIds[0] : undefined,
    );
    const getSecondModelPortfolioReturnsQuery = useGetModelPortfoliosMonthlyReturns(
        fundsSelectionContext.selectionType === 'Portfolio' ? fundsSelectionContext.funds.selectedIds[1] : undefined,
    );
    const getThirdModelPortfolioReturnsQuery = useGetModelPortfoliosMonthlyReturns(
        fundsSelectionContext.selectionType === 'Portfolio' ? fundsSelectionContext.funds.selectedIds[2] : undefined,
    );

    const fundsComparableChunk = useMemo(() => {
        // If one is still loading, do not calculate the lot.
        if (
            getFirstFundRatesQuery.status === 'loading' ||
            getSecondFundRatesQuery.status === 'loading' ||
            getThirdFundRatesQuery.status === 'loading'
        )
            return [];

        const results = [getFirstFundRatesQuery.data, getSecondFundRatesQuery.data, getThirdFundRatesQuery.data].filter(
            (r) => !!r,
        ) as GetFundRatesResponse[];

        return getComparableChunk(
            results.map((r) => {
                return {
                    id: r.fundId + '',
                    rates: r.rates,
                };
            }),
        );
    }, [getFirstFundRatesQuery.data, getSecondFundRatesQuery.data, getThirdFundRatesQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resets the interval selection to the full range when isLoading is set to true
    useEffect(() => {
        // This is needed to set the initial interval to the maximum range of available dates
        if (fundsComparableChunk.length > 0 && fundsComparableChunk[0].rates.length > 0) {
            chartIntervalContext.select('Custom', {
                startDate: DateTime.max(
                    ...fundsSelectionContext.funds.selected.map(o => DateTime.fromISO(o.basicCharacteristics.startDate))
                ),
                endDate: DateTime.fromISO(fundsComparableChunk[0].rates[0].date),
            });
        }
    }, [fundsComparableChunk, chartIntervalContext.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const fundChartData = useMemo<(AssetRates | undefined)[]>(() => {
        if (chartIntervalContext.isLoading) return [];
        return fundsComparableChunk.map((res) => {
            const fund = fundsSelectionContext.funds.selected.find((f) => f.id === res.id);

            // If fund is not in selection, no data point should be shown.
            if (fund === undefined) return undefined;

            const timeValueDataPoints = ratesToTimeValueDataPoints(res.rates);

            const intervalDataPoints = timeValueDataPointsWithinInterval(timeValueDataPoints, chartIntervalContext.selected.interval)

            const firstRate = intervalDataPoints[intervalDataPoints.length - 1];
            const lastRate = intervalDataPoints[0];

            const highestLowestRate = getHighestLowest(intervalDataPoints);

            if (fund.category === 'Bond' || fund.category === 'Stock') {
                const indexed = indexedFundTimeValueDataPoints(
                    intervalDataPoints,
                    fund.dividendPayments?.map((p) => {
                        return {
                            date: DateTime.fromISO(p.date),
                            amount: p.amountPerParticipation,
                        };
                    }) || [],
                );

                return {
                    id: res.id,
                    withinInterval: {
                        indexed: thinnedOutOnDate(indexed),
                        highestRaw: highestLowestRate.highest,
                        lowestRaw: highestLowestRate.lowest,
                        first: chartIntervalContext.selected.startDate 
                            ? { date: chartIntervalContext.selected.startDate } 
                            : (firstRate ? { date: DateTime.fromMillis(firstRate.t)} : undefined),
                        last: lastRate
                            ? {
                                  date: DateTime.fromMillis(lastRate.t),
                                  raw: lastRate.y,
                                  indexed: indexed[0].y,
                              }
                            : undefined,
                    },
                    allTime: {
                        raw: timeValueDataPoints,
                    },
                };
            }

            return undefined;
        });
    }, [selectionKey, fundsComparableChunk, chartIntervalContext.selected, chartIntervalContext.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const modelPortfolioComparableChunk = useMemo(() => {
        // If one is still loading, do not calculate the lot.
        if (
            getFirstModelPortfolioReturnsQuery.status === 'loading' ||
            getSecondModelPortfolioReturnsQuery.status === 'loading' ||
            getThirdModelPortfolioReturnsQuery.status === 'loading'
        )
            return [];

        const results = [
            getFirstModelPortfolioReturnsQuery.data,
            getSecondModelPortfolioReturnsQuery.data,
            getThirdModelPortfolioReturnsQuery.data,
        ].filter((r) => !!r) as MonthlyReturnRatesResponse[];

        return getComparableChunk(
            results.map((r) => {
                return {
                    id: r.modelPortfolioId + '',
                    rates: r.monthlyReturnRates,
                };
            }),
        );
        /* eslint-disable */
    }, [
        getFirstModelPortfolioReturnsQuery.data,
        getSecondModelPortfolioReturnsQuery.data,
        getThirdModelPortfolioReturnsQuery.data,
    ]); /* eslint-enable */

    // Resets the interval selection to the full range when isLoading is set to true
    useEffect(() => {
        if (modelPortfolioComparableChunk.length > 0 && modelPortfolioComparableChunk[0].rates.length > 0) {
            const model = fundsSelectionContext.funds.selected[0];
            chartIntervalContext.select('Custom', {
                startDate: DateTime.fromISO(
                    model.basicCharacteristics.startDate,
                ),
                endDate: DateTime.fromISO(modelPortfolioComparableChunk[0].rates[0].date),
            });
        }
    }, [modelPortfolioComparableChunk, chartIntervalContext.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const modelPortfolioChartData = useMemo<(AssetRates | undefined)[]>(() => {
        return modelPortfolioComparableChunk.map((res) => {
            const fund = fundsSelectionContext.funds.selected.find((f) => f.id === res.id);

            // If fund is not in selection, no data point should be shown.
            if (fund === undefined) return undefined;

            const timeValueDataPoints = res.rates.map((r) => {
                return {
                    t: DateTime.fromISO(r.date).toMillis(),
                    y: r.return, // Return values are percentages where 100% = 1.
                };
            });

            const withinInterval = timeValueDataPointsWithinInterval(
                timeValueDataPoints,
                chartIntervalContext.selected.interval,
            );
            
            const indexed = indexedPortfolioTimeValueDataPoints(withinInterval);
            // Although in selection, for a ModelPortfolio there are only monthly point, so at the start of a year it can 
            // there are no data points as BND didn't put in December from last year yet.
            if (indexed.length === 0) return undefined

            const highestLowestRate = getHighestLowest(withinInterval);
            const firstRate = DateTime.fromMillis(indexed[indexed.length - 1].t) ;

            return {
                id: res.id,
                withinInterval: {
                    indexed,
                    raw: withinInterval,
                    highestRaw: highestLowestRate.highest,
                    lowestRaw: highestLowestRate.lowest,
                    first: { date: firstRate},
                    last: {
                              date: DateTime.fromMillis(indexed[0].t),
                              raw: withinInterval[0].y,
                              indexed: indexed[0].y,
                          }
                },
                allTime: {
                    raw: timeValueDataPoints,
                },
            };
        });
    }, [selectionKey, chartIntervalContext.isLoading, chartIntervalContext.selected, modelPortfolioComparableChunk]); // eslint-disable-line react-hooks/exhaustive-deps

    const all = fundsSelectionContext.selectionType === 'Portfolio' ? modelPortfolioChartData : fundChartData;

    return (
        <FundRatesContext.Provider
            value={{
                isLoading:
                    getFirstFundRatesQuery.isLoading ||
                    getSecondFundRatesQuery.isLoading ||
                    getThirdFundRatesQuery.isLoading ||
                    getFirstModelPortfolioReturnsQuery.isLoading ||
                    getSecondModelPortfolioReturnsQuery.isLoading ||
                    getThirdModelPortfolioReturnsQuery.isLoading,
                rates: {
                    all,
                    get: (id: string) => {
                        return all.find((a) => a?.id === id);
                    },
                },
            }}
        >
            {children}
        </FundRatesContext.Provider>
    );
};

const getHighestLowest = (data: TimeValueDataPoint[]) => {
    return data.reduce(
        (current, { y }) => {
            if (y > current.highest) {
                return {
                    ...current,
                    highest: y,
                };
            }
            if (y < current.lowest) {
                return {
                    ...current,
                    lowest: y,
                };
            }
            return current;
        },
        { highest: 0, lowest: data[0]?.y || 0 },
    );
};

const ratesToTimeValueDataPoints = (rates: Rate[]) => {
    return rates.map((r) => {
        return {
            t: DateTime.fromISO(r.date).toMillis(),
            y: r.nav * 100, // Nav rates are not returned in cents from the backend, but in euros. Convert to cents.
        };
    });
};
